import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';
import { NarwhalQueryParameters } from '../../api/narwhal.query';
import { NarwhalService } from '../../narwhal.service';
import { defs } from '../../defs';
import { Generation, Sex } from 'src/app/shared/filter/filter';

const sortByTranslationKeys = [
	'sortByOptions.like',
	'sortByOptions.productivity',
	'sortByOptions.age',
	'sortByOptions.fitness',
	'sortByOptions.priceInGtr'
];

@Component({
	selector: 'narwhalia-population-sidebar',
	templateUrl: './population-sidebar.component.html',
	styleUrls: ['./population-sidebar.component.scss']
})
export class PopulationSidebarComponent implements OnInit {
	populationFilter: FormGroup;
	sortByParams: any[];
	tmpClanName: string;
	tmpOwnerName: string;
	totalCount: number;

	constructor(
		private fb: FormBuilder,
		private router: Router,
		private route: ActivatedRoute,
		private narwhalService: NarwhalService,
		private translateService: TranslateService
	) {
		this.translateService.get(sortByTranslationKeys).subscribe(sortByOptions => {
			const [like, productivity, age, fitness, priceInGtr] = sortByTranslationKeys;
			this.sortByParams = [
				{ viewValue: sortByOptions[like], value: 'like' },
				{ viewValue: sortByOptions[productivity], value: 'productivity' },
				{ viewValue: sortByOptions[age], value: 'age' },
				{ viewValue: sortByOptions[fitness], value: 'fitness' },
				{ viewValue: sortByOptions[priceInGtr], value: 'price' }
			];
		});
		this.createForm();
		this.narwhalService.totalCount$.subscribe(totalCount => (this.totalCount = totalCount));
		this.route.queryParams.subscribe((params: NarwhalQueryParameters) => {
			const { clanName, ownerName } = params;
			this.tmpClanName = clanName;
			this.populationFilter.get('clanName').setValue(clanName);
			this.tmpOwnerName = ownerName;
			this.populationFilter.get('ownerName').setValue(ownerName);
		});
	}

	createForm() {
		this.populationFilter = this.fb.group({
			sortBy: [defs.sortOrders.defaults.population],
			sortOrder: [''],
			clanName: [''],
			ownerName: [''],
			generation: [''],
			forSale: [undefined],
			exclusive: [undefined],
			male: [false],
			female: [false],
			firstGen: [false],
			secondGen: [false]
		});
	}

	patchForm = queryParams => {
		const { forSale, clanName, ownerName, exclusive, gender, gen, sortBy } = queryParams;
		this.populationFilter.patchValue({
			forSale: forSale === 'true',
			clanName,
			ownerName,
			exclusive: exclusive === 'true',
			male: Sex.isMale(gender),
			female: Sex.isFemale(gender),
			firstGen: Generation.firstGen(gen),
			secondGen: Generation.secondGen(gen),
			sortBy
		});
	};

	refreshUrlQueryParams = ({ forSale, clanName, ownerName, exclusive, male, female, firstGen, secondGen, sortBy }) => {

		let queryParams: NarwhalQueryParameters = {
			gender: Sex.from(male, female),
			gen: Generation.from(firstGen, secondGen),
			sortBy
		};

		if (this.tmpClanName) {
			queryParams = { ...queryParams, clanName: this.tmpClanName };
		}

		if (this.tmpOwnerName) {
			queryParams = { ...queryParams, ownerName: this.tmpOwnerName };
		}

		if (forSale) {
			queryParams = { ...queryParams, forSale };
		}

		if (exclusive) {
			queryParams = { ...queryParams, exclusive };
		}

		this.router.navigate(['population'], { queryParams });
	};

	submitClanName() {
		this.tmpClanName = this.populationFilter.get('clanName').value;
		this.refreshUrlQueryParams({ ...this.populationFilter.value });
	}

	submitOwnerName() {
		this.tmpOwnerName = this.populationFilter.get('ownerName').value;
		this.refreshUrlQueryParams({ ...this.populationFilter.value });
	}

	handleGenderChange() {
		this.checkContrary('male', 'female');
		this.checkContrary('female', 'male');
	}

	handleGenerationChange() {
		this.checkContrary('firstGen', 'secondGen');
		this.checkContrary('secondGen', 'firstGen');
	}

	checkContrary(firstFormControl, secondFormControl) {
		this.populationFilter
			.get(firstFormControl)
			.valueChanges.subscribe(
				secondGenValue =>
					secondGenValue &&
					this.populationFilter.get(secondFormControl).value &&
					this.populationFilter.get(secondFormControl).setValue(false)
			);
	}

	ngOnInit() {
		this.narwhalService.populationQueryParams$.pipe(first(queryParams => Object.keys(queryParams).length > 0)).subscribe(queryParams => {
			// init the form
			this.patchForm(queryParams);
			// subscribe to changes after the patch is done
			this.populationFilter.valueChanges.subscribe(this.refreshUrlQueryParams);
			this.handleGenderChange();
			this.handleGenerationChange();
		});
	}
}